// Cache utilities for managing cache policies and operations

import { CachePolicy } from '../types/index.js';
import { getTTL } from '../config/offline-config.js';

const API_BASE_URL =
  (typeof import.meta !== 'undefined' && import.meta.env?.VITE_API_BASE_URL) ||
  'http://localhost:3000';

/**
 * Build a cache key that matches service-layer URLs (same as base.api fullUrl).
 * Use this for IndexedDB persistence from hooks so reads/writes share one key.
 */
export function buildFullApiCacheKey(path, params = {}) {
  const normalizedPath = path.startsWith('http') ? path : `${API_BASE_URL}${path}`;
  const [basePath, existingQs] = normalizedPath.split('?');
  if (existingQs) {
    return normalizedPath;
  }
  const entries = Object.keys(params).filter(
    (k) => params[k] !== undefined && params[k] !== null && params[k] !== ''
  );
  if (entries.length === 0) {
    return basePath;
  }
  const qs = entries
    .map((k) => `${encodeURIComponent(k)}=${encodeURIComponent(params[k])}`)
    .join('&');
  return `${basePath}?${qs}`;
}

/**
 * Generate cache key from URL and parameters (legacy — prefer buildFullApiCacheKey for API data)
 */
export function generateCacheKey(url, params = {}) {
  const paramString = Object.keys(params)
    .sort()
    .map(key => `${key}=${JSON.stringify(params[key])}`)
    .join('&');
  
  return paramString ? `${url}?${paramString}` : url;
}

/**
 * Check if cache entry is expired
 */
export function isExpired(entry) {
  if (!entry?.metadata?.expiresAt) return false;
  return entry.metadata.expiresAt < Date.now();
}

/**
 * Check if cache entry is stale (older than staleTime)
 */
export function isStale(entry, staleTime = 5 * 60 * 1000) {
  if (!entry?.metadata?.updatedAt) return true;
  const age = Date.now() - entry.metadata.updatedAt;
  return age > staleTime;
}

/**
 * Calculate cache TTL based on entity type (single source: offline-config).
 */
export function getCacheTTL(entityType) {
  return getTTL(entityType);
}

/**
 * Determine cache policy based on request type.
 *
 * IMPORTANT: React Query queryFns resolve synchronously per refetch. Policies that
 * return IndexedDB immediately (CACHE_FIRST / broken SWR) cause invalidateQueries
 * refetches to resolve to stale data while online — the UI never picks up mutations.
 *
 * All GETs use NETWORK_FIRST: online requests hit the network first, then update
 * IndexedDB; offline uses the local cache when the network is unavailable.
 */
export function getCachePolicy(method, url) {
  if (method === 'GET') {
    return CachePolicy.NETWORK_FIRST;
  }

  if (method === 'POST' && /search(ByName|Persons?)$/i.test(url)) {
    return CachePolicy.NETWORK_FIRST;
  }

  return CachePolicy.NETWORK_ONLY;
}

/**
 * Parse cache key to extract URL and params
 */
export function parseCacheKey(cacheKey) {
  const [url, paramString] = cacheKey.split('?');
  
  if (!paramString) {
    return { url, params: {} };
  }

  const params = {};
  paramString.split('&').forEach(pair => {
    const [key, value] = pair.split('=');
    try {
      params[key] = JSON.parse(decodeURIComponent(value));
    } catch {
      params[key] = decodeURIComponent(value);
    }
  });

  return { url, params };
}

/**
 * Extract entity type from URL
 */
export function extractEntityType(url) {
  const patterns = {
    athletes: /athlete(s)?/,
    trainers: /trainer(s)?/,
    programs: /program(s)?/,
    diets: /diet(s)?/,
    daybooks: /dayBook(s)?/,
    gyms: /gym(s)?/,
    memberships: /membership(s)?/,
    settings: /settings?/,
    payments: /payment(s)?/,
    lockers: /locker(s)?/,
    notifications: /notification(s)?/,
    exercises: /exercises?/,
    meals: /meals?/,
  };

  for (const [type, pattern] of Object.entries(patterns)) {
    if (pattern.test(url)) {
      return type;
    }
  }

  return 'default';
}

/**
 * Merge cached data with fresh data
 */
export function mergeData(cached, fresh) {
  if (!cached) return fresh;
  if (!fresh) return cached;

  // If both are arrays, merge by ID
  if (Array.isArray(cached) && Array.isArray(fresh)) {
    const merged = [...fresh];
    const freshIds = new Set(fresh.map(item => item.id));
    
    cached.forEach(cachedItem => {
      if (!freshIds.has(cachedItem.id)) {
        merged.push(cachedItem);
      }
    });

    return merged;
  }

  // If both are objects, merge deeply (prefer fresh fields)
  if (typeof cached === 'object' && typeof fresh === 'object') {
    return { ...cached, ...fresh };
  }

  // Otherwise, prefer fresh data
  return fresh;
}

/**
 * Compress data for storage (simple JSON compression)
 * In production, consider using a proper compression library
 */
export async function compressData(data) {
  try {
    const json = JSON.stringify(data);
    // For now, just return the JSON string
    // In production, use something like lz-string
    return json;
  } catch (error) {
    console.error('[CacheUtils] Compression failed:', error);
    return data;
  }
}

/**
 * Decompress data from storage
 */
export async function decompressData(compressed) {
  try {
    if (typeof compressed === 'string') {
      return JSON.parse(compressed);
    }
    return compressed;
  } catch (error) {
    console.error('[CacheUtils] Decompression failed:', error);
    return compressed;
  }
}

/**
 * Estimate data size in bytes
 */
export function estimateSize(data) {
  try {
    return new Blob([JSON.stringify(data)]).size;
  } catch {
    return 0;
  }
}

/**
 * Format bytes to human-readable format
 */
export function formatBytes(bytes) {
  if (bytes === 0) return '0 Bytes';
  
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  
  return Math.round(bytes / Math.pow(k, i) * 100) / 100 + ' ' + sizes[i];
}

/**
 * Validate cache entry structure
 */
export function validateCacheEntry(entry) {
  if (!entry) return false;
  if (!entry.data) return false;
  if (!entry.metadata) return false;
  if (!entry.metadata.entityType) return false;
  if (!entry.metadata.updatedAt) return false;
  
  return true;
}

/**
 * Clean up old cache entries based on LRU
 */
export async function cleanupLRU(entries, maxSize) {
  if (entries.length <= maxSize) return [];

  // Sort by last accessed time
  const sorted = [...entries].sort((a, b) => 
    (a.metadata?.lastAccessedAt || 0) - (b.metadata?.lastAccessedAt || 0)
  );

  // Remove oldest entries
  const toRemove = sorted.slice(0, entries.length - maxSize);
  return toRemove.map(entry => entry.metadata?.id || entry.id);
}

/**
 * Batch operations for performance
 */
export async function batchSet(items, repository) {
  const results = await Promise.allSettled(
    items.map(item => repository.save(item.id, item.data, item.options))
  );

  const successful = results.filter(r => r.status === 'fulfilled').length;
  const failed = results.filter(r => r.status === 'rejected').length;

  return { successful, failed };
}

/**
 * Batch get operations
 */
export async function batchGet(ids, repository) {
  const results = await Promise.allSettled(
    ids.map(id => repository.getById(id))
  );

  const successful = [];
  const failed = [];

  results.forEach((result, index) => {
    if (result.status === 'fulfilled' && result.value) {
      successful.push({ id: ids[index], data: result.value });
    } else {
      failed.push(ids[index]);
    }
  });

  return { successful, failed };
}
